import React from 'react';
import {
  Firestore,
  doc,
  getDoc,
  getDocs,
  query,
  collection,
  QueryConstraint,
  where,
  limit,
  startAfter,
  orderBy,
  updateDoc,
  DocumentSnapshot,
  runTransaction,
} from 'firebase/firestore/lite';
import { useFirestoreClient } from 'src/firebase';
import { MCustomerRepository } from 'src/repositories/mCustomer/interface';
import { Collection } from 'src/models/common';
import { toMCustomerFromDoc, toUpdateDocFromMCustomer } from './converter';
import {
  MCustomerGetItemQuery,
  MCustomerUpdateItemQuery,
} from 'src/usecases/mCustomer/reader';
import { getFieldName, MCustomer } from 'src/models/mCustomer';
import {
  LoginAccountState,
  useLoginAccount,
} from 'src/globalStates/loginAccount';
import { validData } from 'src/util/validate';
import { BulkUpdateResult, CSVUpdateData } from 'src/usecases/mCustomer/usecase';

export const useMCustomerRepository = (): MCustomerRepository => {
  const db = useFirestoreClient();
  const account = useLoginAccount();
  return React.useMemo(
    () => createMCustomerRepository(db, account),
    [db, account]
  );
};

const createMCustomerRepository = (
  db: Firestore,
  account: LoginAccountState
) => {
  return {
    async getDoc({ mCustomerId }: MCustomerGetItemQuery) {
      if (account.merchantId === undefined || mCustomerId === undefined) {
        throw new Error('エラーが発生しました');
      }

      const docRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MCustomers
        }/${mCustomerId.toString()}`
      );

      const snapshot = await getDoc(docRef);

      if (!snapshot.exists) {
        return null;
      }

      const _mCustomer = toMCustomerFromDoc(snapshot.data()!, snapshot.id);
      if (
        !_mCustomer.storeIds.some((storeId) =>
          account.storeIds?.includes(storeId)
        )
      ) {
        return null;
      }

      if (!validData(_mCustomer.storeIds, account)) {
        throw new Error('権限がありません');
      }

      return _mCustomer;
    },

    // 必要なmCustomerのデータを取得する
    async getDocs(mCustomers: MCustomerGetItemQuery[]) {
      const COLLECTION_CUSTOMER_LIMIT_COUNT = 200;
      const MAX_IN_CLAUSE_COUNT = 20;

      if (account.merchantId === undefined) {
        throw new Error('エラーが発生しました');
      }

      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId?.toString()}/${
          Collection.MCustomers
        }`
      );
      const queries: Array<QueryConstraint> = [
        where(getFieldName('documentStatus'), '==', 'active'),
        orderBy(getFieldName('createdAt'), 'desc'),
      ];

      // 来店したことある店舗に権限がある店舗IDが存在するか(マスターアカウントは不要)
      if (!account.master) {
        queries.push(
          where(
            getFieldName('storeIds'),
            'array-contains-any',
            account.storeIds ?? []
          )
        );
      }

      if  (mCustomers.length === 0) {
        return [];
      }

      // 取得データ格納用と判別用変数
      const allMCustomerData: MCustomer[] = [];
      let lastDoc: DocumentSnapshot | null = null;
      let hasMore = true;
      let totalFetched = 0;

      // クエリのin句の制限が30件、firebaseの設定ルールに合わせて20件ごとに取得する
      for (let mCusIdsCnt = 0; mCusIdsCnt < mCustomers.length; mCusIdsCnt += MAX_IN_CLAUSE_COUNT) {
        // 変数を初期化
        lastDoc = null;
        hasMore = true;

        // 各件ごとに取得する
        const chunkedMCusIds = mCustomers.slice(mCusIdsCnt, mCusIdsCnt + MAX_IN_CLAUSE_COUNT).map((mCustomer) => mCustomer.mCustomerId !== undefined ? mCustomer.mCustomerId : '');
        
        // 作成済クエリ部分を格納
        const pushMCusIdsQueries = [...queries];
        pushMCusIdsQueries.push(where('__name__', 'in', chunkedMCusIds));

        // 必ずchunkedMCusIdsの数しか取得しないため、ページング処理は不要
        const querySnapshot = await getDocs(query(collRef, ...pushMCusIdsQueries, limit(COLLECTION_CUSTOMER_LIMIT_COUNT)));
        // 取得したトランザクションをマッピング
        const docs = querySnapshot.docs.map((_doc) => toMCustomerFromDoc(_doc.data()!, _doc.id));
        // 取得したトランザクションを全体に追加
        allMCustomerData.push(...docs);
      }

      return allMCustomerData;
    },

    async getAllDocs() {
      if (account.merchantId === undefined) {
        throw new Error('エラーが発生しました');
      }

      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId?.toString()}/${
          Collection.MCustomers
        }`
      );

      const queries: Array<QueryConstraint> = [
        where(getFieldName('documentStatus'), '==', 'active'),
        orderBy(getFieldName('createdAt'), 'desc'),
      ];

      if (!account.master) {
        queries.push(
          where(
            getFieldName('storeIds'),
            'array-contains-any',
            account.storeIds ?? []
          )
        );
      }

      const data: MCustomer[] = [];

      for (let index = 0; index < 10000; index++) {
        queries.push(
          startAfter(data[data.length - 1]?.createdAt ?? new Date())
        );
        const _snap = (
          await getDocs(query(collRef, ...queries, limit(200)))
        ).docs.map((_doc) => toMCustomerFromDoc(_doc.data()!, _doc.id));

        data.push(..._snap);
        if (_snap.length < 200) {
          break;
        }
      }

      return data.filter((_v) => validData(_v.storeIds, account));
    },

    async updateDoc({
      mCustomer,
    }: MCustomerUpdateItemQuery): Promise<MCustomer> {
      if (account.merchantId === undefined) {
        throw new Error('エラーが発生しました');
      }

      if (!validData(mCustomer.storeIds, account)) {
        throw new Error('権限がありません');
      }

      const docRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MCustomers
        }/${mCustomer.id}`
      );

      await updateDoc(docRef, toUpdateDocFromMCustomer(mCustomer));

      const _updatedMCus = await this.getDoc({ mCustomerId: mCustomer.id! });
      if (_updatedMCus === null) {
        throw new Error('エラーが発生しました');
      }

      return _updatedMCus;
    },

    async bulkUpdateByLineIds(csvData: CSVUpdateData[]): Promise<BulkUpdateResult> {
      if (account.merchantId === undefined) {
        throw new Error('エラーが発生しました');
      }
    
      const MAX_IN_CLAUSE = 20;
      
      const results: BulkUpdateResult = {
        success: 0,
        failed: 0,
        errors: [],
        updatedMCustomerIds: [], // 追加
      };
    
      try {
        // 1. LINE_IDから既存の顧客データを検索
        const collRef = collection(
          db,
          `${Collection.Merchants}/${account.merchantId.toString()}/${
            Collection.MCustomers
          }`
        );
    
        const lineIds = csvData.map(data => data.lineId);
        const existingCustomers = new Map<string, MCustomer>();
    
        // LINE_IDで顧客を検索（in句の制限で分割処理）
        for (let i = 0; i < lineIds.length; i += MAX_IN_CLAUSE) {
          const chunk = lineIds.slice(i, i + MAX_IN_CLAUSE);
          
          const q = query(
            collRef,
            where(getFieldName('lineId'), 'in', chunk),
            where(getFieldName('documentStatus'), '==', 'active'),
            limit(200)
          );
    
          try {
            const querySnapshot = await getDocs(q);
            
            querySnapshot.docs.forEach(docSnap => {
              const customer = toMCustomerFromDoc(docSnap.data(), docSnap.id);
              
              if (customer.lineId && validData(customer.storeIds, account)) {
                existingCustomers.set(customer.lineId, customer);
              }
            });
          } catch (queryError) {
            const chunkErrorMsg = `データベース接続エラーが発生しました（LINE_ID: ${chunk.join(', ')}）`;
            results.errors.push(chunkErrorMsg);
          }
        }
    
        // 2. 1件ずつトランザクションで更新処理
        for (const data of csvData) {
          try {
            const existingCustomer = existingCustomers.get(data.lineId);
            
            if (!existingCustomer) {
              results.failed++;
              results.errors.push(`LINE_ID: ${data.lineId} - 顧客が見つかりません（削除済みまたは存在しない可能性があります）`);
              continue;
            }
    
            // トランザクションで更新処理
            const docRef = doc(
              db,
              `${Collection.Merchants}/${account.merchantId.toString()}/${
                Collection.MCustomers
              }/${existingCustomer.id}`
            );
            
            await runTransaction(db, async (transaction) => {
              // トランザクション内で最新データを取得
              const docSnapshot = await transaction.get(docRef);
              
              if (!docSnapshot.exists()) {
                throw new Error('顧客データが存在しません');
              }
              
              // 最新データで権限チェック
              const latestCustomer = toMCustomerFromDoc(docSnapshot.data(), docSnapshot.id);
              if (!validData(latestCustomer.storeIds, account)) {
                throw new Error('権限がありません');
              }
              
              // 最新データをベースに更新データを準備
              let updatedCustomer: MCustomer = { ...latestCustomer };
    
              // ニックネーム更新
              if (data.nickname?.trim()) {
                updatedCustomer = { ...updatedCustomer, nickname: data.nickname.trim() };
              }
    
              // 備考追記
              if (data.remarks?.trim()) {
                const timestamp = new Date().toLocaleDateString('ja-JP');
                const existingRemarks = latestCustomer.remarks ?? '';
                
                updatedCustomer = { 
                  ...updatedCustomer, 
                  remarks: existingRemarks 
                    ? `${existingRemarks}\n[${timestamp}] ${data.remarks.trim()}`
                    : `[${timestamp}] ${data.remarks.trim()}` 
                };
              }
              
              // トランザクション内で更新実行
              transaction.update(docRef, toUpdateDocFromMCustomer(updatedCustomer));
            });
            
            results.success++;
            if (existingCustomer.id) {
              results.updatedMCustomerIds.push(existingCustomer.id);
            }
            
          } catch (error) {
            results.failed++;
            const errorMsg = error instanceof Error ? error.message : '更新処理でエラーが発生しました';
            results.errors.push(`LINE_ID: ${data.lineId} - ${errorMsg}`);
          }
        }
    
        return results;
    
      } catch (error) {
        throw new Error('一括更新処理中にエラーが発生しました');
      }
    }
  };
};
